import React from 'react';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { NodeId } from '@craftjs/core';

interface PageSelectFieldProps {
  optionId: string;
  pages: Array<{ id: string; name: string }>;
  isPagesLoading: boolean;
  onPageChange: (pageId: string) => void;
}

export const PageSelectField: React.FC<PageSelectFieldProps> = ({
  optionId,
  pages,
  isPagesLoading,
  onPageChange,
}) => {
  return (
    <SelectField
      classNameContent="max-h-[400px]"
      className="w-full max-w-[216px]"
      placeholder="Select a page"
      text="Page"
      defaultValue={optionId}
      onValueChange={onPageChange}
    >
      {isPagesLoading ? (
        <SelectItem value="loading" disabled>
          Loading pages...
        </SelectItem>
      ) : pages.length === 0 ? (
        <SelectItem value="no-pages" disabled>
          No pages available
        </SelectItem>
      ) : (
        pages.map((page) => (
          <SelectItem key={page.id} value={page.id}>
            {page.name || `Page ${page.id.slice(0, 8)}`}
          </SelectItem>
        ))
      )}
    </SelectField>
  );
};

interface ExternalUrlFieldProps {
  externalUrl: string;
  onChangeUrl: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSanitizeUrl: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const ExternalUrlField: React.FC<ExternalUrlFieldProps> = ({
  externalUrl,
  onChangeUrl,
  onSanitizeUrl,
}) => {
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="text-button-sm font-medium text-neutral-900">URL</p>
      </div>
      <TextField
        className="w-full max-w-[216px]"
        classNameInputParent="!w-full !max-w-[216px]"
        inputValue={externalUrl}
        onBlur={onSanitizeUrl}
        onChange={onChangeUrl}
        placeholderText="https://www.domain.com"
        state="default"
        text=""
        type="text"
      />
    </>
  );
};

interface LinkOptionsFieldsProps {
  linkOptions: {
    newTab: boolean;
    useNofollow: boolean;
  };
  onToggleOption: (option: string) => void;
}

export const LinkOptionsFields: React.FC<LinkOptionsFieldsProps> = ({
  linkOptions,
  onToggleOption,
}) => {
  return (
    <>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="new-tab"
          checked={linkOptions.newTab}
          onCheckedChange={() => onToggleOption('newTab')}
        />
        <Label className="inline text-xs text-neutral-500" htmlFor="new-tab">
          <span>Open in a new window</span>
        </Label>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="use-nofollow"
          checked={linkOptions.useNofollow}
          onCheckedChange={() => onToggleOption('useNofollow')}
        />
        <Label className="inline text-xs text-neutral-500" htmlFor="use-nofollow">
          <span>Use nofollow</span>
        </Label>
      </div>
    </>
  );
};

interface SectionSelectFieldProps {
  optionId: string;
  sections: Array<{ id: NodeId; label: string }>;
  onSectionChange: (sectionId: string) => void;
}

export const SectionSelectField: React.FC<SectionSelectFieldProps> = ({
  optionId,
  sections,
  onSectionChange,
}) => {
  return (
    <SelectField
      classNameContent="max-h-[400px]"
      className="w-full max-w-[216px]"
      defaultValue={optionId}
      placeholder="Select section"
      text="Section"
      onValueChange={onSectionChange}
    >
      {sections.map((section) => (
        <SelectItem key={section.id} value={section.id}>
          {section.label}
        </SelectItem>
      ))}
    </SelectField>
  );
};

interface PopupSelectFieldProps {
  optionId: string;
  popups: Array<{ id: NodeId; label: string }>;
  onPopupChange: (popupId: string) => void;
}

export const PopupSelectField: React.FC<PopupSelectFieldProps> = ({
  optionId,
  popups,
  onPopupChange,
}) => {
  return (
    <SelectField
      className="w-full max-w-[216px]"
      size="large"
      defaultValue={optionId}
      onValueChange={onPopupChange}
      placeholder="Select popup"
      text="Popup"
    >
      {popups.map((popup) => (
        <SelectItem key={popup.id} value={popup.id}>
          {popup.label}
        </SelectItem>
      ))}
    </SelectField>
  );
};
